/**
 * Page-level title row, inside the content column (not the sidebar — see
 * PageTemplate/PageHeader.js for the old top bar this replaces).
 * Spec: assets/handoffs/design_handoff_app_shell, Rules — shell.
 *
 * @param {Array<{label,to}>=} breadcrumbs - rendered only past one level
 *   deep; a single crumb repeating the title is not a trail
 * @param {string} title - one h1 per page
 * @param {string=} subtitle - one line
 * @param {ReactNode=} actions - page-level buttons, right-aligned on the
 *   title row
 * @param {boolean=false} compact - builder-type pages carrying an action bar
 * @param {boolean=false} noDivider - suppress the header's own bottom
 *   divider, for pages where a tab bar immediately below already supplies it
 *   (TabbedSettingsPage)
 */
import React from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import Icon from "../../../theme/untitled/icons/Icon";
import styles from "./PageHeader.module.scss";

const PageHeader = ({ breadcrumbs = [], title, subtitle, actions, compact = false, noDivider = false }) => (
  <div
    className={classNames(styles.root, {
      [styles["root--compact"]]: compact,
      [styles["root--noDivider"]]: noDivider,
    })}
  >
    {breadcrumbs.length > 1 && (
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        {breadcrumbs.map((crumb, index) => {
          const isCurrent = index === breadcrumbs.length - 1;
          return (
            <span key={crumb.to || crumb.label} className={styles.crumbSegment}>
              {index > 0 && <Icon name="ChevronRight" className={styles.crumbSeparator} />}
              {isCurrent || !crumb.to ? (
                <span className={styles.crumbCurrent}>{crumb.label}</span>
              ) : (
                <Link to={crumb.to} className={styles.crumbLink}>
                  {crumb.label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>
    )}

    <div className={styles.titleRow}>
      <div className={styles.titleGroup}>
        <h1 className={styles.title}>{title}</h1>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  </div>
);

export default PageHeader;
